import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { Download, Calendar, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { KpiCard } from '@/components/KpiCard';
import { PageHeader } from '@/components/PageHeader';
import { EmptyState } from '@/components/EmptyState';
import { getAnalyticsSummary, getAnalyticsTimeseries, getAnalyticsChannels, getExportUrl } from '@/lib/api';
import { useAuth } from '@/lib/AuthContext';

const CHANNEL_NAMES = {
  meta: 'Meta',
  google: 'Google',
  tiktok: 'TikTok',
  apple_search_ads: 'Apple Search Ads'
};

const fadeIn = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0, transition: { duration: 0.3 } } };

export default function ClientReportsPage() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [timeseries, setTimeseries] = useState([]);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30');

  useEffect(() => {
    loadData();
  }, [period]);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (user?.client_id) params.client_id = user.client_id;
      
      // Calculate date range
      const now = new Date();
      const from = new Date(now);
      from.setDate(from.getDate() - parseInt(period));
      params.date_from = from.toISOString().slice(0, 10);
      params.date_to = now.toISOString().slice(0, 10);

      const [sumRes, tsRes, chRes] = await Promise.all([
        getAnalyticsSummary(params),
        getAnalyticsTimeseries(params),
        getAnalyticsChannels(params)
      ]);
      setSummary(sumRes.data);
      setTimeseries(tsRes.data);
      setChannels(chRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    const params = {};
    if (user?.client_id) params.client_id = user.client_id;
    const url = getExportUrl('analytics', params);
    window.open(url, '_blank');
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-pulse text-sm text-slate-500">Report wird geladen...</div></div>;
  }

  return (
    <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.05 } } }}>
      <PageHeader title="Reports" description="Detaillierte Kampagnen-Berichte und CSV-Export">
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Letzte 7 Tage</SelectItem>
            <SelectItem value="14">Letzte 14 Tage</SelectItem>
            <SelectItem value="30">Letzte 30 Tage</SelectItem>
            <SelectItem value="60">Letzte 60 Tage</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={handleExport} variant="outline" className="btn-lift" data-testid="export-csv-button">
          <Download className="w-4 h-4 mr-2" /> CSV Export
        </Button>
      </PageHeader>

      {!summary || summary.total_installs === 0 ? (
        <EmptyState title="Keine Report-Daten" description="Für den gewählten Zeitraum liegen keine Daten vor." />
      ) : (
        <>
          {/* KPIs */}
          <motion.div variants={fadeIn} className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4 mb-6">
            <KpiCard label="Installs" value={summary.total_installs} />
            <KpiCard label="Avg. CPI" value={summary.avg_cpi} prefix="€" />
            <KpiCard label="Spend" value={summary.total_spend} prefix="€" />
            <KpiCard label="Revenue" value={summary.total_revenue} prefix="€" />
            <KpiCard label="ROAS" value={summary.roas} suffix="x" />
          </motion.div>

          {/* Trend Chart */}
          <motion.div variants={fadeIn} className="rounded-xl border border-slate-200 bg-white p-4 sm:p-6 mb-6">
            <h3 className="font-display text-base font-semibold text-slate-900 mb-4">Performance Trend ({period} Tage)</h3>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={timeseries}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94a3b8' }} tickFormatter={v => v?.slice(5)} />
                <YAxis yAxisId="left" tick={{ fontSize: 11, fill: '#94a3b8' }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: '#94a3b8' }} />
                <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '12px' }} />
                <Area yAxisId="left" type="monotone" dataKey="installs" stroke="#2BAE9B" fill="#2BAE9B" fillOpacity={0.1} strokeWidth={2} name="Installs" />
                <Area yAxisId="right" type="monotone" dataKey="spend" stroke="#2A6FDB" fill="#2A6FDB" fillOpacity={0.05} strokeWidth={2} name="Spend (€)" />
              </AreaChart>
            </ResponsiveContainer>
          </motion.div>

          {/* Channel Table */}
          <motion.div variants={fadeIn} className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            <div className="p-4 border-b border-slate-100">
              <h3 className="font-display text-base font-semibold text-slate-900">Kanal-Aufschlüsselung ({period} Tage)</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-slate-100 bg-slate-50/50">
                  <th className="text-left p-3 pl-4 font-medium text-slate-500">Kanal</th>
                  <th className="text-right p-3 font-medium text-slate-500">Installs</th>
                  <th className="text-right p-3 font-medium text-slate-500">Spend (€)</th>
                  <th className="text-right p-3 font-medium text-slate-500">Revenue (€)</th>
                  <th className="text-right p-3 font-medium text-slate-500">CPI (€)</th>
                  <th className="text-right p-3 pr-4 font-medium text-slate-500">ROAS</th>
                </tr></thead>
                <tbody>
                  {channels.map((ch, i) => (
                    <tr key={i} className="border-b border-slate-50 last:border-0 hover:bg-slate-50">
                      <td className="p-3 pl-4 font-medium text-slate-900">{CHANNEL_NAMES[ch.channel] || ch.channel}</td>
                      <td className="p-3 text-right tabular-nums">{ch.installs?.toLocaleString('de-DE')}</td>
                      <td className="p-3 text-right tabular-nums">{ch.spend?.toLocaleString('de-DE', { minimumFractionDigits: 2 })}</td>
                      <td className="p-3 text-right tabular-nums">{ch.revenue?.toLocaleString('de-DE', { minimumFractionDigits: 2 })}</td>
                      <td className="p-3 text-right tabular-nums">{ch.cpi?.toFixed(2)}</td>
                      <td className="p-3 pr-4 text-right">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          ch.roas >= 1.5 ? 'bg-emerald-50 text-emerald-700' : ch.roas >= 1 ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700'
                        }`}>
                          {ch.roas?.toFixed(2)}x
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        </>
      )}
    </motion.div>
  );
}
